import { ExpressionSyntaxError, tokenize } from './tokenizer.js';
export const FORBIDDEN_PROPERTIES = new Set([
    '__proto__',
    'prototype',
    'constructor',
]);
export const DEFINED_LITERAL = Symbol('defined');
const KEYWORD_LITERALS = new Map([
    ['true', true],
    ['false', false],
    ['null', null],
    ['undefined', undefined],
    ['defined', DEFINED_LITERAL],
]);
const BINARY_PRECEDENCE = {
    '||': 1,
    '&&': 2,
    '==': 3,
    '!=': 3,
    '===': 3,
    '!==': 3,
    '<': 4,
    '<=': 4,
    '>': 4,
    '>=': 4,
    in: 4,
    '+': 5,
    '-': 5,
    '*': 6,
    '/': 6,
    '%': 6,
};
export const AGGREGATE_NAMES = new Set(['min', 'max', 'sum', 'count']);
export const AGGREGATE_RECEIVERS = new Set([
    'series',
    'displaySet',
    'displaySets',
]);
function aggregateReceiver(node) {
    if (node.type === 'member' &&
        AGGREGATE_NAMES.has(node.property) &&
        node.object.type === 'member' &&
        node.object.property === 'instances' &&
        node.object.object.type === 'identifier' &&
        AGGREGATE_RECEIVERS.has(node.object.object.name)) {
        return node.object.object.name;
    }
    return undefined;
}
class Parser {
    constructor(source, expression) {
        this.position = 0;
        this.expression = expression;
        this.tokens = tokenize(source, expression);
    }
    peek() {
        return this.tokens[this.position];
    }
    next() {
        return this.tokens[this.position++];
    }
    isPunct(value) {
        const token = this.peek();
        return token.kind === 'punct' && token.value === value;
    }
    isIdent(name) {
        const token = this.peek();
        return token.kind === 'ident' && token.name === name;
    }
    expectPunct(value) {
        if (!this.isPunct(value)) {
            throw new ExpressionSyntaxError(`Expected '${value}'`, this.expression);
        }
        this.next();
    }
    fail(message) {
        throw new ExpressionSyntaxError(message, this.expression);
    }
    parse() {
        const node = this.parseConditional();
        if (this.peek().kind !== 'end') {
            this.fail('Unexpected trailing input');
        }
        return node;
    }
    parseSubExpression() {
        return this.parseConditional();
    }
    parseConditional() {
        const test = this.parseBinary(0);
        if (!this.isPunct('?')) {
            return test;
        }
        this.next();
        const consequent = this.parseConditional();
        this.expectPunct(':');
        const alternate = this.parseConditional();
        return { type: 'conditional', test, consequent, alternate };
    }
    parseBinary(minPrecedence) {
        let left = this.parseUnary();
        for (;;) {
            const token = this.peek();
            let operator;
            if (token.kind === 'punct' && BINARY_PRECEDENCE[token.value]) {
                operator = token.value;
            }
            else if (token.kind === 'ident' && token.name === 'in') {
                operator = 'in';
            }
            if (!operator) {
                return left;
            }
            const precedence = BINARY_PRECEDENCE[operator];
            if (precedence <= minPrecedence) {
                return left;
            }
            this.next();
            const right = this.parseBinary(precedence);
            left =
                operator === '&&' || operator === '||'
                    ? { type: 'logical', operator, left, right }
                    : { type: 'binary', operator, left, right };
        }
    }
    parseUnary() {
        const token = this.peek();
        if (token.kind === 'punct' &&
            (token.value === '!' || token.value === '-' || token.value === '+')) {
            this.next();
            return {
                type: 'unary',
                operator: token.value,
                argument: this.parseUnary(),
            };
        }
        return this.parsePostfix();
    }
    parsePostfix() {
        let node = this.parsePrimary();
        for (;;) {
            if (this.isPunct('.')) {
                this.next();
                const property = this.next();
                if (property.kind !== 'ident') {
                    this.fail("Expected a property name after '.'");
                }
                if (FORBIDDEN_PROPERTIES.has(property.name)) {
                    this.fail(`Access to '${property.name}' is not allowed`);
                }
                node = { type: 'member', object: node, property: property.name };
                continue;
            }
            if (this.isPunct('[')) {
                this.next();
                const index = this.parseSubExpression();
                this.expectPunct(']');
                node = { type: 'index', object: node, index };
                continue;
            }
            if (this.isPunct('(')) {
                const receiver = aggregateReceiver(node);
                if (node.type !== 'identifier' && !receiver) {
                    this.fail('Only whitelisted helper functions may be called');
                }
                const callee = node.type === 'identifier'
                    ? node.name
                    : node.property;
                this.next();
                const args = [];
                if (!this.isPunct(')')) {
                    for (;;) {
                        args.push(this.parseSubExpression());
                        if (this.isPunct(',')) {
                            this.next();
                            continue;
                        }
                        break;
                    }
                }
                this.expectPunct(')');
                node = receiver
                    ? { type: 'call', callee, args, receiver }
                    : { type: 'call', callee, args };
                continue;
            }
            return node;
        }
    }
    parsePrimary() {
        const token = this.next();
        if (token.kind === 'number') {
            return { type: 'literal', value: token.value };
        }
        if (token.kind === 'string') {
            return { type: 'literal', value: token.value };
        }
        if (token.kind === 'template') {
            return {
                type: 'template',
                parts: token.parts.map((part) => part.kind === 'text'
                    ? part
                    : {
                        kind: 'expr',
                        node: parseExpressionSource(part.source, this.expression),
                    }),
            };
        }
        if (token.kind === 'ident') {
            if (KEYWORD_LITERALS.has(token.name)) {
                return { type: 'literal', value: KEYWORD_LITERALS.get(token.name) };
            }
            if (token.name === 'in') {
                this.fail("Unexpected 'in'");
            }
            if (FORBIDDEN_PROPERTIES.has(token.name)) {
                this.fail(`Access to '${token.name}' is not allowed`);
            }
            return { type: 'identifier', name: token.name };
        }
        if (token.kind === 'punct') {
            if (token.value === '(') {
                const node = this.parseSubExpression();
                this.expectPunct(')');
                return node;
            }
            if (token.value === '[') {
                const elements = [];
                if (!this.isPunct(']')) {
                    for (;;) {
                        elements.push(this.parseSubExpression());
                        if (this.isPunct(',')) {
                            this.next();
                            continue;
                        }
                        break;
                    }
                }
                this.expectPunct(']');
                return { type: 'array', elements };
            }
        }
        this.fail('Unexpected token');
    }
}
export function parseExpressionSource(source, expression = source) {
    return new Parser(source, expression).parse();
}
